/* External */
import {
  memo, useCallback, useEffect, useMemo, useRef,
} from 'react';
import {
  Switch, Route, Redirect, useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

/* Components */
import { UPDATE_PROFILES } from '~/storage/profiles/actionTypes';
import ValidationPage from './ValidationPage';
import Interstitial from './Interstitial';
import Home from './Home';
import Live from './Live';
import OnDemand from './OnDemand';
import Sports from './Sports';
import Kids from './Kids';
import Search from './Search';
import Setting from './Setting';
import Player from './Player';
import Catalog from './Catalog';
import Lander from './Lander';
import MyList from './MyList';
import MyPage from './MyPage';
import AcceptTermsOfService from './AcceptTermsOfService';
import TermsOfServiceErrorPage from './AcceptTermsOfService/TermsOfServiceErrorPage';
import Detail from './Detail';
import TermsOfService from '../../components/TermsOfService';
import UserProfile from './Profiles/UserProfile/UserProfile';
import Onboarding from './Profiles/Onboarding/Onboarding';
import CreateProfileName from './Profiles/CreateProfileName/CreateProfileName';
import CreateAllowedContent from './Profiles/CreateAllowedContent/CreateAllowedContent';
import CreateProfileConfirmation from './Profiles/CreateProfileConfirmation/CreateProfileConfirmation';
import CreateProfileResume from './Profiles/CreateProfileResume/CreateProfileResume';
import ManagementProfile from './Profiles/ManagementProfile/ManagementProfile';
import ConfirmDeleteProfile from './Profiles/ConfirmDeleteProfile/ConfirmDeleteProfile';
import CreateProfile from './Profiles/CreateProfile/CreateProfile';
import ProfilesErrorPage from './Profiles/ProfilesErrorPage/ProfilesErrorPage';
import PlayerErrorPage from './Player/PlayerErrorPage/PlayerErrorPage';
import DefaultCreateProfile from './Profiles/defaultProfileFlow/DefaultCreateProfile/DefaultCreateProfile';
import DefaultCreateProfileName from './Profiles/defaultProfileFlow/DefaultCreateProfileName/DefaultCreateProfileName';

// Hooks
import { useCloseAppCompletely } from '../../hooks/useShouldCloseAppCompletely';

/* Storage */
import { fetchAllChannels } from '../../storage/channels';
import { resetPdpHistory, resetBackButtonHistory } from '../../storage/pdpHistory';

/* Utils */
import { getLink, getPath, Routes } from '../../utils/router';
import { TYPE_ACTIONS_PROFILE } from '../../utils/constants';
import { featureFlagParentalControl, getProfileSelected } from '../../utils/profiles';
import platform from '../../utils/platform';
import { isSpideoMoreLikeThisActive, channelMinimumSizeToFetch } from '../../utils/env';
import { selectAllChannels } from '../../storage/channels/selectors';

/* Providers */
import { useCountry } from '../../providers/ConfigProvider';
import {
  useIsLoggedIn, useIsLoadingUserAccount, useIsFrontPorchUser,
} from '../../providers/UserProvider';
import { MessageId } from '../../components/FullScreenMessage';
import { useOnBackground, useIsForegroundApp } from '../../providers/ForegroundAppProvider';
import { checkToken } from '../../services/authService';

const frontPorchMessageUrl = getLink(Routes.MessagePage, {
  query: { messageKey: MessageId.FRONT_PORCH },
});

const selectProfilesLink = getLink(
  Routes.Private.Profiles,
);

const minimumChannelSize = channelMinimumSizeToFetch || 5;

function Private() {
  const dispatch = useDispatch();
  const country = useCountry();
  const { pathname, search, hash } = useLocation();
  const isRefreshing = useRef(false);
  const loginLink = useMemo(() => getLink(
    Routes.Public.PinLogin,
    { query: { redirectTo: `${pathname}${search}${hash}` } },
  ), [hash, pathname, search]);

  const channels = useSelector(selectAllChannels);
  const isLoggedIn = useIsLoggedIn();
  const isLoadingUser = useIsLoadingUserAccount();
  const isFrontPorch = useIsFrontPorchUser();
  const hasParentalControl = featureFlagParentalControl(country);
  const isForegroundApp = useIsForegroundApp();
  const profileSelected = useMemo(getProfileSelected, [pathname]);
  const detailPageUrlRegex = useMemo(() => /.*\/detail\/.*/, []);
  const playerPageUrlRegex = useMemo(() => /.*\/player\/.*/, []);
  const isNotDetailPageAndPlayer = !detailPageUrlRegex.test(pathname)
    && !playerPageUrlRegex.test(pathname);

  useEffect(() => {
    if (isSpideoMoreLikeThisActive && isNotDetailPageAndPlayer) {
      dispatch(resetPdpHistory());
      dispatch(resetBackButtonHistory());
    }
  }, [dispatch, isNotDetailPageAndPlayer]);

  useEffect(() => {
    if (!isLoadingUser && isLoggedIn && channels?.size <= minimumChannelSize) {
      dispatch(fetchAllChannels());
    }
  }, [dispatch, isLoadingUser, isLoggedIn, channels, pathname]);

  useEffect(() => {
    const performCheckToken = async () => {
      if (!isLoggedIn || isRefreshing.current || !isForegroundApp) return;

      isRefreshing.current = true;
      try {
        const updateProfiles = await checkToken();
        if (updateProfiles) {
          dispatch({ type: UPDATE_PROFILES });
        }
      } finally {
        isRefreshing.current = false;
      }
    };

    performCheckToken();
  }, [isLoggedIn, pathname, isForegroundApp, dispatch]);

  const shouldCloseAppCompletely = useCloseAppCompletely();

  const conditionallyCloseApp = useCallback(() => {
    if (shouldCloseAppCompletely) {
      platform.closeApp();
    }
  }, [shouldCloseAppCompletely]);

  useOnBackground(conditionallyCloseApp);

  /* eslint-disable-next-line unicorn/no-null
      -- React v16 doesn't support returning undefined in components */
  if (isLoadingUser) return null;

  return (
    <Switch>
      {!isLoggedIn && <Redirect to={loginLink} />}
      {!profileSelected && pathname !== selectProfilesLink && <Redirect to={selectProfilesLink} />}
      {isFrontPorch && <Redirect to={frontPorchMessageUrl} />}
      <Route exact path={getPath(Routes.Private)}>
        <ValidationPage />
      </Route>
      <Route path={getPath(Routes.Private.Interstitial)}>
        <Interstitial />
      </Route>
      <Route exact path={getPath(Routes.Private.AcceptTermsOfService)}>
        <AcceptTermsOfService />
      </Route>
      <Route exact path={getPath(Routes.Private.AcceptTermsOfService.Error)}>
        <TermsOfServiceErrorPage />
      </Route>
      <Route exact path={getPath(Routes.Private.TermsOfService)}>
        <TermsOfService />
      </Route>
      <Route exact path={getPath(Routes.Private.Home)}>
        <Home />
      </Route>
      <Route path={getPath(Routes.Private.Live)}>
        <Live />
      </Route>
      <Route path={getPath(Routes.Private.OnDemand)}>
        <OnDemand />
      </Route>
      <Route path={getPath(Routes.Private.Sports)}>
        <Sports />
      </Route>
      <Route path={getPath(Routes.Private.Kids)}>
        <Kids />
      </Route>
      <Route exact path={getPath(Routes.Private.Search)}>
        <Search />
      </Route>
      <Route path={getPath(Routes.Private.Setting)}>
        <Setting />
      </Route>
      <Route path={getPath(Routes.Private.Player.Error)}>
        <PlayerErrorPage />
      </Route>
      <Route path={getPath(Routes.Private.Player)}>
        <Player />
      </Route>
      <Route path={getPath(Routes.Private.Detail)}>
        <Detail />
      </Route>
      <Route path={getPath(Routes.Private.Catalog)}>
        <Catalog />
      </Route>
      <Route path={getPath(Routes.Private.MyList)}>
        <MyList />
      </Route>
      <Route path={getPath(Routes.Private.MyPage)}>
        <MyPage />
      </Route>
      <Route path={getPath(Routes.Private.Lander)}>
        <Lander />
      </Route>
      <Route exact path={getPath(Routes.Private.Profiles)}>
        <UserProfile />
      </Route>
      <Route path={getPath(Routes.Private.Onboarding)}>
        <Onboarding />
      </Route>
      <Route exact path={getPath(Routes.Private.Profiles.Management)}>
        <ManagementProfile />
      </Route>
      <Route path={getPath(Routes.Private.Profiles.Delete)}>
        <ConfirmDeleteProfile />
      </Route>
      <Route exact path={getPath(Routes.Private.Profiles.Edit)}>
        {hasParentalControl
          ? <CreateProfile typeAction={TYPE_ACTIONS_PROFILE.EDIT} />
          : <DefaultCreateProfile typeAction={TYPE_ACTIONS_PROFILE.EDIT} />}
      </Route>
      <Route exact path={getPath(Routes.Private.Profiles.Create)}>
        {hasParentalControl
          ? <CreateProfile typeAction={TYPE_ACTIONS_PROFILE.CREATE} />
          : <DefaultCreateProfile typeAction={TYPE_ACTIONS_PROFILE.CREATE} />}
      </Route>
      <Route path={getPath(Routes.Private.Profiles.Create.Name)}>
        {hasParentalControl
          ? <CreateProfileName />
          : <DefaultCreateProfileName />}
      </Route>
      <Route path={getPath(Routes.Private.Profiles.Create.AllowedContent)}>
        <CreateAllowedContent />
      </Route>
      <Route path={getPath(Routes.Private.Profiles.Create.Confirmation)}>
        <CreateProfileConfirmation />
      </Route>
      <Route path={getPath(Routes.Private.Profiles.Create.Resume)}>
        <CreateProfileResume />
      </Route>
      <Route path={getPath(Routes.Private.Profiles.Edit.Name)}>
        {hasParentalControl
          ? <CreateProfileName />
          : <DefaultCreateProfileName />}
      </Route>
      <Route path={getPath(Routes.Private.Profiles.Edit.AllowedContent)}>
        <CreateAllowedContent />
      </Route>
      <Route path={getPath(Routes.Private.Profiles.Error)}>
        <ProfilesErrorPage />
      </Route>
    </Switch>
  );
}

export default memo(Private);
